// ProgressContext.js
//
// Thin React binding over the upload manager (src/upload/uploadManager.js).
//
// The queue used to live in this component's useState, which meant a reload or an
// unmounted provider erased every trace of an in-flight upload. State now belongs to
// the manager singleton; this context only mirrors it into React and re-exposes the
// actions the UI needs.
import { createContext, useContext, useEffect, useState } from "react";
import * as uploadManager from "../upload/uploadManager";

const ProgressContext = createContext(null);

export const ProgressProvider = ({ children }) => {
  const [uploadQueue, setUploadQueue] = useState(() => uploadManager.getItems());

  useEffect(() => {
    // Bring back anything that was in flight when the tab was last closed, so an
    // interrupted 30GB upload is visible and resumable instead of silently gone.
    uploadManager.restore();
    return uploadManager.subscribe(setUploadQueue);
  }, []);

  // Only a byte transfer is at risk from closing the tab — a transcode continues at
  // Bunny regardless, so we don't nag about those.
  const hasActiveTransfers = uploadQueue.some((item) => item.phase === "uploading");

  useEffect(() => {
    if (!hasActiveTransfers) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasActiveTransfers]);

  return (
    <ProgressContext.Provider
      value={{
        uploadQueue,
        hasActiveTransfers,
        enqueueUpload: uploadManager.enqueue,
        enqueueProcessing: uploadManager.enqueueProcessing,
        pauseUpload: uploadManager.pause,
        resumeUpload: uploadManager.resume,
        cancelUpload: uploadManager.cancel,
        dismissUpload: uploadManager.dismiss,
        dismissFinishedUploads: uploadManager.dismissFinished,
        setGridRefresh: uploadManager.setGridRefresh,
        trackEncodingProgress: uploadManager.trackEncoding,
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgressContext = () => {
  const context = useContext(ProgressContext);
  if (!context)
    throw new Error("useProgressContext must be used within ProgressProvider");
  return context;
};
